import { useRolodexEntries } from 'features/rolodex'
import { useMemo } from 'react'
import {
  FormEntry,
  KVAnnotation,
  KVInspectionArea,
  RolodexEntryType,
} from 'types'

/**
 * Hook to calculate work orders per inspection area.
 *
 * Returns a map of inspectionAreaId -> FormEntry[] for all work orders
 * associated with annotations in that inspection area.
 */
export function useInspectionAreaWorkOrders(
  kestrelModelId: string | undefined,
  annotations: KVAnnotation[] | undefined,
  inspectionAreas: KVInspectionArea[] | undefined
): {
  workOrdersMap: Record<string, FormEntry[]>
  isLoading: boolean
} {
  const form781AId = useMemo(
    () => (kestrelModelId ? `${kestrelModelId}-781a` : undefined),
    [kestrelModelId]
  )

  const { entries: form781AEntries, hasEverLoaded } = useRolodexEntries(
    form781AId
      ? { type: RolodexEntryType.FORM_781A, ids: [form781AId] }
      : undefined
  )

  const form781AEntry = form781AEntries[0]

  const workOrdersMap = useMemo(() => {
    const map: Record<string, FormEntry[]> = {}

    if (inspectionAreas) {
      inspectionAreas.forEach((ia) => {
        map[ia.id] = []
      })
    }

    if (!form781AEntry || !annotations || !inspectionAreas) {
      return map
    }

    const formData = form781AEntry.data.formData as
      | { entries?: FormEntry[] }
      | undefined
    const formEntries = (formData?.entries || []) as FormEntry[]

    formEntries.forEach((entry) => {
      if (!entry.annotationId) return

      const annotation = annotations.find((a) => a.id === entry.annotationId)
      if (!annotation) return

      const inspectionAreaId = annotation.tags?.inspectionArea?.value
      if (!inspectionAreaId) return

      if (!map[inspectionAreaId]) {
        map[inspectionAreaId] = []
      }
      map[inspectionAreaId].push(entry)
    })

    return map
  }, [form781AEntry, annotations, inspectionAreas])

  return {
    workOrdersMap,
    isLoading: !hasEverLoaded,
  }
}
